import { createBrowserRouter, Navigate, Outlet, ScrollRestoration } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { AppShell } from './components/layout/AppShell';
import { useAuthStore } from './stores/authStore';

function AuthGuard() {
  const { isAuthenticated, isLoading } = useAuth();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-obsidian">
        <div className="flex flex-col items-center gap-4">
          <div className="logo-coin flex h-14 w-14 items-center justify-center rounded-full">
            <span className="font-display text-2xl font-extrabold text-obsidian">P</span>
          </div>
          <div className="h-1 w-24 overflow-hidden rounded-full bg-white/10">
            <div className="h-full w-1/2 animate-pulse rounded-full bg-copper" />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppShell
      userName={user?.name}
      userRole={user?.role}
      onLogout={logout}
    >
      <Outlet />
      <ScrollRestoration />
    </AppShell>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    async lazy() {
      const { default: Component } = await import('./pages/MarketingTeaserPage');
      return { Component };
    },
  },
  {
    path: '/pricing',
    async lazy() {
      const { default: Component } = await import('./pages/PricingPage');
      return { Component };
    },
  },
  {
    element: <AuthGuard />,
    children: [
      {
        path: '/app',
        async lazy() {
          const { default: Component } = await import('./pages/CommandPage');
          return { Component };
        },
      },
      {
        path: '/analytics',
        async lazy() {
          const { default: Component } = await import('./pages/AnalyticsPage');
          return { Component };
        },
      },
      {
        path: '/pipeline',
        async lazy() {
          const { default: Component } = await import('./pages/SalesPipelinePage');
          return { Component };
        },
      },
      {
        path: '/clients',
        async lazy() {
          const { default: Component } = await import('./pages/ClientsPage');
          return { Component };
        },
      },
      {
        path: '/clients/:id',
        async lazy() {
          const { default: Component } = await import('./pages/ClientDetailPage');
          return { Component };
        },
      },
      {
        path: '/jobs/:id',
        async lazy() {
          const { default: Component } = await import('./pages/JobWorkspacePage');
          return { Component };
        },
      },
      {
        path: '/inbox',
        async lazy() {
          const { default: Component } = await import('./pages/TenantInboxPage');
          return { Component };
        },
      },
      {
        path: '/calls',
        async lazy() {
          const { default: Component } = await import('./pages/PhoneCallsPage');
          return { Component };
        },
      },
      {
        path: '/calendar',
        async lazy() {
          const { default: Component } = await import('./pages/AppointmentCalendarPage');
          return { Component };
        },
      },
      {
        path: '/jarvis',
        async lazy() {
          const { default: Component } = await import('./pages/JarvisPage');
          return { Component };
        },
      },
      {
        path: '/estimates/notes',
        async lazy() {
          const { default: Component } = await import('./pages/NotesToQuotePage');
          return { Component };
        },
      },
      {
        path: '/estimates/:id',
        async lazy() {
          const { default: Component } = await import('./pages/QuoteBuilderPage');
          return { Component };
        },
      },
      {
        path: '/estimates/:id/color-book',
        async lazy() {
          const { default: Component } = await import('./pages/ColorBookPage');
          return { Component };
        },
      },
      {
        path: '/invoices',
        async lazy() {
          const { default: Component } = await import('./pages/InvoicePage');
          return { Component };
        },
      },
      {
        path: '/ai-log',
        async lazy() {
          const { default: Component } = await import('./pages/AiActivityLogPage');
          return { Component };
        },
      },
      {
        path: '/settings',
        async lazy() {
          const { default: Component } = await import('./pages/SwitchboardSettingsPage');
          return { Component };
        },
      },
      {
        path: '/settings/company',
        async lazy() {
          const { default: Component } = await import('./pages/CompanySettingsPage');
          return { Component };
        },
      },
      {
        path: '/settings/catalog',
        async lazy() {
          const { default: Component } = await import('./pages/ServiceCatalogPage');
          return { Component };
        },
      },
      {
        path: '/settings/component-rates',
        async lazy() {
          const { default: Component } = await import('./pages/ComponentRatesPage');
          return { Component };
        },
      },
      {
        path: '/settings/team',
        async lazy() {
          const { default: Component } = await import('./pages/PersonnelPage');
          return { Component };
        },
      },
      {
        path: '/dev',
        async lazy() {
          const { default: Component } = await import('./pages/DevPage');
          return { Component };
        },
      },
    ],
  },
  {
    path: '/login',
    async lazy() {
      const { default: Component } = await import('./pages/LoginPage');
      return { Component };
    },
  },
  {
    path: '/recap/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicRecapPage');
      return { Component };
    },
  },
  {
    path: '/view-estimate/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicEstimatePage');
      return { Component };
    },
  },
  {
    path: '/submit-quote/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicQuoteSubmitPage');
      return { Component };
    },
  },
  {
    path: '/q/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicQuoteSubmitPage');
      return { Component };
    },
  },
  {
    path: '/q/:token/photos',
    async lazy() {
      const { default: Component } = await import('./pages/QuotePhotosPage');
      return { Component };
    },
  },
  {
    path: '/colors/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicColorsPage');
      return { Component };
    },
  },
  {
    path: '/intake/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicIntakePage');
      return { Component };
    },
  },
  {
    path: '/color-book/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicColorBookPage');
      return { Component };
    },
  },
  {
    path: '/deposit/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicDepositPage');
      return { Component };
    },
  },
  {
    path: '/schedule/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicSchedulePage');
      return { Component };
    },
  },
  {
    path: '/preproduction/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicPreProductionHubPage');
      return { Component };
    },
  },
  {
    path: '/get-estimate',
    async lazy() {
      const { default: Component } = await import('./pages/PublicBookingPage');
      return { Component };
    },
  },
  {
    path: '/view-invoice/:token',
    async lazy() {
      const { default: Component } = await import('./pages/PublicInvoicePage');
      return { Component };
    },
  },
  {
    path: '*',
    element: <Navigate to="/app" replace />,
  },
]);
